import React, { useState } from "react";
import api from "../../api";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { InvestorEditModal } from "./InvestorEditModals";
import { FiEdit2, FiTrash2, FiCheck, FiUserPlus } from "react-icons/fi";

const InvestorList = ({ investors, selectedId, onSelect, refresh }) => {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [editingInvestor, setEditingInvestor] = useState(null);
  const [loading, setLoading] = useState(false);

  const addInvestor = async () => {
    if (!name.trim()) return;
    setLoading(true);
    try {
      await api.post("/investors", { name });
      toast.success(t("investors.updated"));
      setName("");
      await refresh();
    } catch {
      toast.error(t("investors.updateFailed"));
    } finally {
      setLoading(false);
    }
  };

  const deleteInvestor = async (id) => {
    if (!window.confirm(t("transactions.confirmDelete"))) return;
    setLoading(true);
    try {
      await api.delete(`/investors/${id}`);
      toast.success(t("transactions.deleted"));
      await refresh();
    } catch {
      toast.error(t("transactions.deleteFailed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Add investor row */}
      <div className="flex flex-col md:flex-row gap-2">
        <input
          type="text"
          placeholder={t("investors.title")}
          className="input input-bordered bg-base-200 border-base-300 text-base-content focus:border-primary focus:outline-none flex-1"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && addInvestor()}
          disabled={loading}
        />
        <button
          onClick={addInvestor}
          disabled={loading}
          className="btn btn-primary gap-2 shrink-0"
        >
          <FiUserPlus className="text-base" />
          {loading ? t("buttons.loading") : t("buttons.save")}
        </button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-xl border border-base-200 bg-base-100 shadow-sm">
        <table className="table table-sm">
          <thead className="bg-base-200 border-b border-base-200">
            <tr>
              <th className="text-base-content/50 text-[11px] font-semibold tracking-wider uppercase">
                {t("transactions.category")}
              </th>
              <th className="text-base-content/50 text-[11px] font-semibold tracking-wider uppercase text-right">
                {t("investors.deposits")}
              </th>
              <th className="text-base-content/50 text-[11px] font-semibold tracking-wider uppercase text-right">
                {t("investors.withdrawals")}
              </th>
              <th className="text-base-content/50 text-[11px] font-semibold tracking-wider uppercase text-right">
                {t("investors.balance")}
              </th>
              <th className="text-base-content/50 text-[11px] font-semibold tracking-wider uppercase text-center">
                {t("transactions.actions")}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-base-200">
            {investors.map((inv) => (
              <tr
                key={inv.id}
                className={`hover:bg-base-200/50 transition-colors group ${
                  selectedId === inv.id ? "bg-primary/5" : ""
                }`}
              >
                <td className="text-base-content font-medium">{inv.name}</td>
                <td className="text-right text-base-content/70 tabular-nums">
                  €{inv.totalDeposits.toFixed(2)}
                </td>
                <td className="text-right text-base-content/70 tabular-nums">
                  €{inv.totalWithdrawals.toFixed(2)}
                </td>
                <td className="text-right font-bold text-base-content tabular-nums">
                  {inv.balance.toFixed(2)} €
                </td>
                <td>
                  <div className="flex gap-1 justify-center items-center">
                    <button
                      onClick={() => onSelect(inv)}
                      disabled={loading}
                      className={`w-7 h-7 rounded-lg flex items-center justify-center transition-colors ${
                        selectedId === inv.id
                          ? "bg-primary text-primary-content"
                          : "text-base-content/40 hover:text-base-content hover:bg-base-300"
                      }`}
                      title={t("buttons.select")}
                    >
                      <FiCheck className="text-sm" />
                    </button>
                    <button
                      onClick={() => setEditingInvestor(inv)}
                      disabled={loading}
                      className="w-7 h-7 rounded-lg text-base-content/40 hover:text-base-content hover:bg-base-300 flex items-center justify-center transition-colors"
                      title={t("buttons.edit")}
                    >
                      <FiEdit2 className="text-sm" />
                    </button>
                    <button
                      onClick={() => deleteInvestor(inv.id)}
                      disabled={loading}
                      className="w-7 h-7 rounded-lg text-base-content/40 hover:text-error hover:bg-error/10 flex items-center justify-center transition-colors"
                      title={t("buttons.delete")}
                    >
                      <FiTrash2 className="text-sm" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {investors.length === 0 && (
              <tr>
                <td colSpan={5} className="text-center text-base-content/40 py-10">
                  {t("investors.noData") || "No investor found"}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {editingInvestor && (
        <InvestorEditModal
          investor={editingInvestor}
          onClose={() => setEditingInvestor(null)}
          onUpdated={refresh}
        />
      )}
    </div>
  );
};

export default InvestorList;
